import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { authService } from "../services/index"; // Remove permissionService import
import { sessionUtils, tokenUtils } from "../security";
import { checkAuth, mapBackendToFrontendGroup } from "./authStoreUtils.js";

// Authentication store for managing auth state with Zustand
const useAuthStore = create(
  persist(
    immer((set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      lastActivity: null,
      rememberMe: false,

      // Actions
      setUser: (user) =>
        set((state) => {
          state.error = null;

          // Map backend role/groups to frontend groups
          const roleDisplay = user?.role_display || user?.role;
          const backendGroups = user?.groups || [];

          const mappedFromGroups = backendGroups
            .map(mapBackendToFrontendGroup)
            .filter(Boolean);

          const mappedFromRole = mapBackendToFrontendGroup(roleDisplay);

          const groups =
            mappedFromGroups.length > 0
              ? mappedFromGroups
              : mappedFromRole
                ? [mappedFromRole]
                : [];

          state.user = {
            ...user,
            groups, // Add groups to user object
          };
          state.isAuthenticated = !!state.user;
        }),

      setTokens: (accessToken, refreshToken) =>
        set((state) => {
          state.accessToken = accessToken;
          state.refreshToken = refreshToken;
          state.isAuthenticated = checkAuth(accessToken, refreshToken);
        }),

      setLoading: (isLoading) =>
        set((state) => {
          state.isLoading = isLoading;
        }),

      setError: (error) =>
        set((state) => {
          state.error = error;
        }),

      clearError: () =>
        set((state) => {
          state.error = null;
        }),

      updateLastActivity: () =>
        set((state) => {
          state.lastActivity = Date.now();
        }),

      resetIdleTimeout: () =>
        set((state) => {
          if (!state.rememberMe) {
            state.lastActivity = Date.now();
          }
        }),

      login: async (credentials) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
          state.rememberMe = credentials.rememberMe || false;
        });
        try {
          const response = await authService.login(credentials);
          tokenUtils.setAccessToken(response.data.tokens.access);
          tokenUtils.setRefreshToken(
            response.data.tokens.refresh,
            credentials.rememberMe
          );
          const user = response.data.user;

          // Process user data directly without permissions call
          set((state) => {
            const roleDisplay = user?.role_display || user?.role;
            const backendGroups = user?.groups || [];

            const mappedFromGroups = backendGroups
              .map(mapBackendToFrontendGroup)
              .filter(Boolean);

            const mappedFromRole = mapBackendToFrontendGroup(roleDisplay);

            const finalGroups =
              mappedFromGroups.length > 0
                ? mappedFromGroups
                : mappedFromRole
                  ? [mappedFromRole]
                  : [];

            state.user = {
              ...user,
              groups: finalGroups,
            };
            state.isAuthenticated = true;
            state.isLoading = false;
          });

          if (!credentials.rememberMe) {
            get().resetIdleTimeout();
          }
          return { success: true, user: user };
        } catch (error) {
          const errorData = error.response?.data;
          const errorKey = errorData?.error_key;
          const errorMessage = errorData?.message || error.message;

          set((state) => {
            state.error = errorMessage;
          });

          // Attach error_key to the error object for the component to use
          const enhancedError = new Error(errorMessage);
          enhancedError.errorKey = errorKey;
          enhancedError.response = error.response;

          throw enhancedError;
        } finally {
          set((state) => {
            state.isLoading = false;
          });
        }
      },

      logout: async () => {
        const refreshToken = tokenUtils.getRefreshToken();

        // Clear Zustand state first
        set((state) => {
          state.user = null;
          state.isAuthenticated = false;
          state.error = null;
          state.isLoading = false;
          state.lastActivity = null;
          state.rememberMe = false;
        });

        // Clear persisted tokens and session
        tokenUtils.clearTokens();
        sessionUtils.clearSession();

        try {
          if (refreshToken) {
            await authService.logout(refreshToken);
          }
          return { success: true };
        } catch (error) {
          console.error("Logout error:", error);
          return {
            success: false,
            error: error.response?.data?.message || error.message,
          };
        }
      },

      //Refresh token function
      refreshTokens: async () => {
        const refreshTokenValue = tokenUtils.getRefreshToken();
        if (!refreshTokenValue) {
          throw new Error("Refresh token missing");
        }
        try {
          const response = await authService.refreshToken(refreshTokenValue);
          tokenUtils.setAccessToken(response.accessToken);
          tokenUtils.setRefreshToken(response.refreshToken);
          get().resetIdleTimeout();
          return true;
        } catch (error) {
          tokenUtils.clearTokens();
          sessionUtils.clearSession();
          throw error;
        }
      },

      //send an email request to reset password - returns uid,token
      changePasswordRequest: async (email) => {
        try {
          const response = await authService.changePasswordRequest(email);
          return response;
        } catch (error) {
          set((state) => {
            state.error = error.response?.data?.message || error.message;
          });
          throw error;
        }
      },

      //here we handle the POST to reset the password (payload=uid,token,password,re_password)
      changePassword: async (payload) => {
        try {
          return await authService.changePassword(payload);
        } catch (error) {
          set((state) => {
            state.error = error.response?.data?.message || error.message;
          });
          throw error;
        }
      },

      //Verify Token and Restore Session
      restoreSession: async () => {
        const accessToken = tokenUtils.getAccessToken();
        const refreshToken = tokenUtils.getRefreshToken();

        const { rememberMe } = get();

        if (!accessToken && !refreshToken) {
          return false;
        }

        if (!accessToken && rememberMe && refreshToken) {
          try {
            await get().refreshTokens();
            return true;
          } catch {
            return false;
          }
        }
        set((state) => {
          state.isLoading = true;
        });

        try {
          // Check if token is expired
          if (tokenUtils.isTokenExpired(accessToken)) {
            await get().refreshTokens();
            return true;
          }

          // Verify token with backend and get user data
          const response = await authService.getMe();

          set((state) => {
            const user = response;
            const roleDisplay = user?.role_display || user?.role;
            const backendGroups = user?.groups || [];

            const mappedFromGroups = backendGroups
              .map(mapBackendToFrontendGroup)
              .filter(Boolean);

            const mappedFromRole = mapBackendToFrontendGroup(roleDisplay);

            const groups =
              mappedFromGroups.length > 0
                ? mappedFromGroups
                : mappedFromRole
                  ? [mappedFromRole]
                  : [];

            state.user = {
              ...user,
              groups,
            };
            state.isAuthenticated = true;
          });

          get().resetIdleTimeout();
          return true;
        } catch (error) {
          tokenUtils.clearTokens();
          sessionUtils.clearSession();
          return false;
        } finally {
          set((state) => {
            state.isLoading = false;
          });
        }
      },

      // Utility methods
      getUserRole: () => {
        const { user } = get();
        return user?.role || null;
      },

      isSessionValid: () => {
        const accessToken = tokenUtils.getAccessToken();
        const refreshToken = tokenUtils.getRefreshToken();
        return checkAuth(accessToken, refreshToken);
      },
    })),
    {
      name: "juzoor-auth-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity,
        rememberMe: state.rememberMe,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const access = tokenUtils.getAccessToken();
          const refresh = tokenUtils.getRefreshToken();

          if (state.rememberMe) {
            // Only check if refresh token exists
            if (!refresh) {
              state.user = null;
              state.isAuthenticated = false;
              state.lastActivity = null;
              tokenUtils.clearTokens();
            }
            // Don't clear if refresh token exists - let restoreSession handle it
          } else {
            // If rememberMe is false, check both tokens
            const valid = checkAuth(access, refresh);
            if (!valid) {
              state.user = null;
              state.isAuthenticated = false;
              state.lastActivity = null;
              tokenUtils.clearTokens();
            }
          }
        }
      },
    }
  )
);

export default useAuthStore;
